/**
 * AI 语音接待模块 — 直连 local-vox WebSocket
 *
 * 协议：
 *   握手:  {provider:"widget", params:{agent_id, from, to, inputSampleRate, modalities}}
 *   上行:  binary PCM16LE 16kHz mono frames (100ms = 3200 bytes)
 *   下行:  JSON transcription events + binary TTS PCM/WAV
 *
 * 与 public/widget.js 使用相同的 local-vox 协议。
 */
import { setPanelContent, getShadowRoot, setFabPulse } from "../../ui/container";
import { t } from "../../ui/i18n";
import { renderTextChatPanel } from "../text-chat/render";
import type { Locale } from "../../types";

/* ────── 状态 ────── */
type VoiceState = "idle" | "verifying" | "dialing" | "connected" | "ended" | "error";
type ViewMode = "waveform" | "chat";

let voiceState: VoiceState = "idle";
let viewMode: ViewMode = "waveform";
let manualHangup = false;
let storedWidgetId = "";
let codeSent = false;
let storedLocale: Locale = "zh-CN";
let voiceWs: WebSocket | null = null;
let audioCtx: AudioContext | null = null;
let micStream: MediaStream | null = null;
let scriptNode: ScriptProcessorNode | null = null;

// 动画状态: idle | thinking | responding | volume
let animMode: "idle" | "thinking" | "responding" | "volume" = "idle";

// 手机号 & 验证
let phoneNumber = "";
let phoneVerified = false;
let countdownTimer: ReturnType<typeof setInterval> | null = null;
let countdownSec = 0;

// 音频播放队列
let audioQueue: Float32Array[] = [];
let playing = false;
let nextPlayTime = 0;

// PCM 发送缓冲
let pcmSendBuf: number[] = [];
const PCM_FRAME_BYTES = 3200;

// 通话计时器 & 聊天消息
let timerInterval: ReturnType<typeof setInterval> | null = null;
let callSec = 0;
let chatMessages: Array<{ role: "user" | "agent"; text: string }> = [];

// canvas 动画相关
let animFrameId: number | null = null;
let analyserNode: AnalyserNode | null = null;

/* ────── SVG 图标 ────── */
function svgMic(): string {
  return `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/><path d="M19 10v2a7 7 0 01-6 6.93V22h-2v-3.07A7 7 0 015 12v-2h2v2a5 5 0 0010 0v-2h2z"/></svg>`;
}
function svgHangup(): string {
  return `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M10.68 13.34a15.46 15.46 0 004.14.84 15.46 15.46 0 004.14-.84 1 1 0 011.12.24l2.92 2.92a1 1 0 01-.12 1.42 17.8 17.8 0 01-8.06 3.08 17.8 17.8 0 01-8.06-3.08 1 1 0 01-.12-1.42l2.92-2.92a1 1 0 011.12-.24z"/><line x1="23" y1="1" x2="1" y2="23" stroke="currentColor" stroke-width="2"/></svg>`;
}
function svgCheck(): string {
  return `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="3"><path d="M20 6L9 17l-5-5"/></svg>`;
}

/* ────── 主渲染入口 ────── */
export function renderVoicePanel(locale: Locale, widgetId?: string): void {
  voiceState = "idle";
  viewMode = "waveform";
  storedWidgetId = widgetId || "";
  phoneNumber = "";
  phoneVerified = false;
  chatMessages = [];
  callSec = 0;
  stopMicCapture();
  stopTimer();
  stopCountdown();
  setFabPulse(true);
  closeVoiceConnection();

  const html = buildUI();
  setPanelContent(html);
  bindEvents(locale);
}

/* ────── 关闭连接 ────── */
function closeVoiceConnection(): void {
  voiceWs?.close();
  voiceWs = null;
  audioQueue = [];
  playing = false;
  nextPlayTime = 0;
  if (animFrameId) { cancelAnimationFrame(animFrameId); animFrameId = null; }
  if (audioCtx) { try { audioCtx.close(); } catch {} audioCtx = null; }
  analyserNode = null;
  pcmSendBuf = [];
}

/* ────── 统一 UI 构建 ────── */
function buildUI(): string {
  return /* html */`
<div class="oc-voice" id="oc-voice-root">
  <div id="oc-voice-content"></div>
  <style>${voiceStyles()}</style>
</div>`;
}

/* ────── 更新内容区 ────── */
function updateContent(html: string): void {
  const root = getShadowRoot();
  const ct = root?.getElementById("oc-voice-content");
  if (ct) ct.innerHTML = html;
}

/* ────── 国家/地区码 ────── */
const COUNTRIES: Array<{ code: string; dial: string; name: string }> = [
  { code: "CN", dial: "+86", name: "中国" },
  { code: "US", dial: "+1", name: "United States" },
  { code: "GB", dial: "+44", name: "United Kingdom" },
  { code: "JP", dial: "+81", name: "日本" },
  { code: "KR", dial: "+82", name: "한국" },
  { code: "HK", dial: "+852", name: "香港" },
  { code: "TW", dial: "+886", name: "台湾" },
  { code: "SG", dial: "+65", name: "Singapore" },
  { code: "AU", dial: "+61", name: "Australia" },
  { code: "DE", dial: "+49", name: "Deutschland" },
  { code: "FR", dial: "+33", name: "France" },
  { code: "CA", dial: "+1", name: "Canada" },
  { code: "IN", dial: "+91", name: "India" },
  { code: "BR", dial: "+55", name: "Brasil" },
  { code: "AE", dial: "+971", name: "الإمارات" },
];
let selectedCountry = COUNTRIES[0];

function buildIdleHTML(): string {
  const color = getPrimaryColor();
  const countryOptions = COUNTRIES.map(c =>
    `<option value="${c.code}" ${c.code === selectedCountry.code ? 'selected' : ''}>${c.dial} ${c.name}</option>`
  ).join("");
  return /* html */`
<div class="oc-v-header">🤖 AI 语音接待</div>
<div class="oc-v-phone-row">
  <select id="oc-v-country" class="oc-v-country-sel">${countryOptions}</select>
  <input type="tel" id="oc-v-phone" class="oc-v-input" placeholder="请输入手机号码" value="${escHtml(phoneNumber)}" maxlength="15" />
</div>
<div class="oc-v-code-row">
  <input type="text" id="oc-v-code" class="oc-v-input" placeholder="输入验证码" maxlength="6" />
  <button type="button" id="oc-v-sendcode" class="oc-v-code-btn" ${countdownSec > 0 ? "disabled" : ""}>
    ${countdownSec > 0 ? `${countdownSec}s` : "验证码"}
  </button>
</div>
<button type="button" id="oc-v-start" class="oc-v-start-btn" style="background:${phoneVerified ? color : '#d1d5db'};cursor:${phoneVerified ? 'pointer' : 'not-allowed'};">
  ${svgMic()} <span>开始语音对话</span>
</button>
<p class="oc-v-hint">首次使用需授权麦克风权限</p>
<p id="oc-v-error" class="oc-v-error" style="display:none;"></p>
`;
}

function buildDialingHTML(): string {
  return /* html */`
<div class="oc-v-dialing-ring">
  <div class="oc-v-ring-outer"></div>
  <div class="oc-v-ring-inner">
    <span style="font-size:28px;">📞</span>
  </div>
</div>
<p class="oc-v-dialing-text">正在呼叫 AI 接待员...</p>
<button type="button" id="oc-v-cancel" class="oc-v-cancel-btn">📴 取消</button>
`;
}

function buildConnectedHTML(): string {
  return /* html */`
<div class="oc-v-header-row">
  <span style="font-size:14px;font-weight:600;">🤖 AI 语音接待</span>
  <button type="button" id="oc-v-switch-text" class="oc-v-switch-btn" title="切换到文本对话" style="width:32px;height:32px;border:1px solid var(--oc-border);border-radius:8px;background:var(--oc-bg);font-size:16px;cursor:pointer;">💬</button>
</div>
<div class="oc-v-tabs">
  <button type="button" id="oc-v-tab-waveform" class="oc-v-tab ${viewMode === 'waveform' ? 'active' : ''}">声纹波动</button>
  <button type="button" id="oc-v-tab-chat" class="oc-v-tab ${viewMode === 'chat' ? 'active' : ''}">文字对话</button>
</div>
<div id="oc-v-mode-content">
  ${viewMode === 'waveform' ? buildWaveformContent() : buildChatContent()}
</div>
<div class="oc-v-bottom-bar">
  <button type="button" id="oc-v-hangup" class="oc-v-hangup-btn">🔴 挂断</button>
</div>
`;
}

function buildWaveformContent(): string {
  return /* html */`
<div class="oc-v-wave-container">
  <!-- idle: 脉冲圆 -->
  <div class="oc-v-anim-idle">
    <div class="oc-v-anim-circle"></div>
  </div>
  <!-- thinking: 环状脉冲 -->
  <div class="oc-v-anim-thinking" style="display:none">
    <div class="oc-v-anim-ring"></div>
  </div>
  <!-- responding: brain SVG -->
  <div class="oc-v-anim-responding" style="display:none">
    <svg width="160" height="160" viewBox="-20 0 190 190" xmlns="http://www.w3.org/2000/svg" fill="currentColor" style="color:${getPrimaryColor()}">
      <g class="oc-v-anim-brain">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M129.49 114.51C129.121 116.961 128.187 119.293 126.762 121.322C125.337 123.351 123.461 125.021 121.28 126.2C120.676 126.535 120.043 126.816 119.39 127.04C120.22 138.04 102.74 142.04 93.32 139.42L96.82 151.66L87.82 151.98L72.07 129.43C66.76 130.93 60.49 131.65 56.44 125.15C56.0721 124.553 55.7382 123.935 55.44 123.3C54.4098 123.51 53.3614 123.617 52.31 123.62C49.31 123.62 44.31 122.72 41.77 120.96C39.7563 119.625 38.1588 117.75 37.16 115.55C31.75 116.29 27.16 115.02 24.16 111.88C20.36 107.97 19.28 101.51 21.26 94.58C23.87 85.33 31.81 74.91 47.59 71C48.9589 69.2982 50.5972 67.8322 52.44 66.66C62.35 60.31 78.44 59.76 90.65 65.79C95.3836 64.9082 100.27 65.376 104.75 67.14C113.53 70.43 119.91 77.31 121.11 84.3C123.487 85.5317 125.433 87.4568 126.69 89.82C129.32 94.76 129.69 99.71 127.92 103.71C129.587 107.049 130.138 110.835 129.49 114.51ZM123.01 109.31C121.612 110.048 120.056 110.434 118.475 110.434C116.894 110.434 115.338 110.048 113.94 109.31L114.67 104.46C117.75 104.76 120.26 103.8 121.57 101.83C123.04 99.64 122.81 96.39 120.95 92.9C118.87 88.99 114.38 88.37 111.89 88.34H111.73C105.49 88.34 99.13 91.89 96.56 96.52L92.82 94.73C93.5553 92.3449 94.8046 90.15 96.48 88.3C95.0376 87.0754 93.9474 85.4887 93.3217 83.703C92.696 81.9173 92.5574 79.9971 92.92 78.14L96.61 77.8C96.7789 79.302 97.4 80.7172 98.3911 81.8583C99.3822 82.9994 100.697 83.8125 102.16 84.19C105.238 82.8161 108.58 82.1335 111.95 82.19C112.43 82.19 112.89 82.24 113.36 82.27C110.969 78.0312 107.18 74.7545 102.64 73C91.56 68.7 84.09 75.37 82.38 77.67C78.26 83.19 80.9 88.41 82.91 91.8L79.61 94.8C76.736 92.314 74.8075 88.9127 74.15 85.17C69.92 86.44 64.24 86.17 61.06 80.74L64.06 78.68C67.43 81.2 72.78 80.98 75.32 77.87C75.9252 76.4949 76.6905 75.1959 77.6 74C79.044 72.093 80.7864 70.4316 82.76 69.08C74.47 66.82 62.76 67.19 55.68 71.73C53.7668 72.841 52.192 74.4517 51.1244 76.3895C50.0569 78.3274 49.5368 80.5192 49.62 82.73C49.62 86.3 52.42 91.94 56.19 92.82L54 97.07C51.5946 96.5129 49.4109 95.2487 47.73 93.44L44.48 97.58L41.23 96L44.41 87.68C43.8904 86.064 43.624 84.3774 43.62 82.68C43.628 81.3361 43.7687 79.9963 44.04 78.68C34.04 82.81 29.1 89.68 27.29 95.96C25.9 100.79 26.44 105.15 28.72 107.49C30.53 109.35 33.3 109.79 35.91 109.62L42.91 104.17L45.21 106.11L43.13 112.93C44.22 116.4 47.79 118.19 54.3 116.93C54.6375 114.169 55.7272 111.554 57.45 109.37C58.7133 107.552 60.3846 106.056 62.33 105L65.75 95.79L69.17 95.64L68.8 103.19C74.55 102.6 80.98 103.77 86.97 102.87L88.07 106.87C79.29 110.93 70.3 104.31 62.15 113.04C59.22 116.18 60.34 118.91 62.15 121.66C64.76 125.59 69.66 123.23 74.67 121.66C82.26 119.34 87.77 117.66 98.16 118.51C95.68 113.8 95.92 108.11 99.24 101.85L104.13 103.78C100.7 111.69 103.91 116.27 106.13 118.29C109.56 121.41 114.72 122.35 118.13 120.47C119.436 119.749 120.559 118.737 121.412 117.513C122.265 116.289 122.825 114.885 123.05 113.41C123.275 112.051 123.258 110.663 123 109.31H123.01Z"/>
      </g>
    </svg>
  </div>
  <!-- volume: 声纹柱 -->
  <div class="oc-v-anim-volume" style="display:none">
    <div class="oc-v-bars">
      <div class="oc-v-bar" id="oc-v-bar-0"></div>
      <div class="oc-v-bar" id="oc-v-bar-1"></div>
      <div class="oc-v-bar" id="oc-v-bar-2"></div>
      <div class="oc-v-bar" id="oc-v-bar-3"></div>
    </div>
  </div>
</div>
<div class="oc-v-timer" id="oc-v-timer">${formatTime(callSec)}</div>
<p class="oc-v-status" id="oc-v-status">🟢 通话中 · 正在聆听</p>
`;
}

function buildChatContent(): string {
  return /* html */`
<div class="oc-v-timer" id="oc-v-timer">${formatTime(callSec)}</div>
<div class="oc-v-chat-list" id="oc-v-chat-list">
  ${chatMessages.map(m => buildChatBubble(m)).join("")}
  ${chatMessages.length === 0 ? '<p class="oc-v-chat-empty">对话内容将在此显示</p>' : ''}
</div>
`;
}

function buildChatBubble(m: { role: "user" | "agent"; text: string }): string {
  const isUser = m.role === "user";
  return /* html */`
<div class="oc-v-chat-row ${isUser ? 'is-user' : 'is-agent'}">
  <div class="oc-v-chat-bubble">${escHtml(m.text)}</div>
</div>`;
}

function buildEndedHTML(): string {
  const color = getPrimaryColor();
  return /* html */`
<div class="oc-v-ended-icon">✅</div>
<p class="oc-v-ended-title">通话已结束</p>
<p class="oc-v-ended-duration">通话时长 ${formatTime(callSec)}</p>
<button type="button" id="oc-v-redial" class="oc-v-start-btn" style="background:${color};">
  ${svgMic()} <span>重新对话</span>
</button>
<button type="button" id="oc-v-close" class="oc-v-close-btn">✕ 关闭弹窗</button>
`;
}

/* ────── 样式 ────── */
function voiceStyles(): string {
  const c = getPrimaryColor();
  return `
.oc-v-header { font-size:16px;font-weight:600;color:var(--oc-text);text-align:center;margin-bottom:16px;padding:12px 0; }
.oc-v-phone-row { display:flex;gap:8px;align-items:center;margin-bottom:10px; }
.oc-v-country-sel { padding:10px 8px;border:1px solid var(--oc-border);border-radius:8px;font-size:13px;background:var(--oc-bg);color:var(--oc-text);cursor:pointer;outline:none;min-width:100px; }
.oc-v-input { flex:1;padding:10px 12px;border:1px solid var(--oc-border);border-radius:8px;font-size:14px;background:var(--oc-bg);color:var(--oc-text);outline:none; }
.oc-v-input:focus { border-color:${c}; }
.oc-v-code-btn { padding:10px 14px;border:1px solid ${c};border-radius:8px;background:transparent;color:${c};font-size:13px;font-weight:600;cursor:pointer;white-space:nowrap;transition:all 0.15s; }
.oc-v-code-btn:hover { background:${c}10; }
.oc-v-code-btn:disabled { border-color:#d1d5db;color:#9ca3af;cursor:not-allowed;background:transparent; }
.oc-v-code-row { display:flex;gap:8px;align-items:center;margin-bottom:16px; }
.oc-v-start-btn { width:100%;padding:14px;border:none;border-radius:12px;font-size:16px;font-weight:600;color:#fff;display:flex;align-items:center;justify-content:center;gap:8px;transition:all 0.15s; }
.oc-v-start-btn:hover:not(:disabled) { opacity:0.9; }
.oc-v-hint { font-size:11px;color:var(--oc-text-secondary);text-align:center;margin-top:12px; }
.oc-v-error { font-size:13px;color:#ef4444;text-align:center;margin-top:8px; }

.oc-v-dialing-ring { display:flex;align-items:center;justify-content:center;height:120px;position:relative;margin:8px 0; }
.oc-v-ring-outer { width:100px;height:100px;border-radius:50%;border:3px solid ${c};animation:oc-ring-pulse 1.5s ease-out infinite;position:absolute; }
.oc-v-ring-inner { width:64px;height:64px;border-radius:50%;background:${c}15;display:flex;align-items:center;justify-content:center;z-index:1; }
@keyframes oc-ring-pulse { 0%{transform:scale(0.9);opacity:1;} 100%{transform:scale(1.5);opacity:0;} }
.oc-v-dialing-text { text-align:center;font-size:14px;color:var(--oc-text-secondary);margin-bottom:16px; }
.oc-v-cancel-btn { width:100%;padding:12px;border:1px solid var(--oc-border);border-radius:10px;background:var(--oc-bg);color:var(--oc-text-secondary);font-size:14px;cursor:pointer; }

.oc-v-header-row { display:flex;align-items:center;justify-content:space-between;margin-bottom:8px; }
.oc-v-switch-btn { display:flex;align-items:center;justify-content:center;transition:all 0.15s; }
.oc-v-switch-btn:hover { background:var(--oc-border)!important; }
.oc-v-tabs { display:flex;border-bottom:1px solid var(--oc-border);margin-bottom:12px; }
.oc-v-tab { flex:1;padding:10px;border:none;background:transparent;font-size:13px;font-weight:500;color:var(--oc-text-secondary);cursor:pointer;text-align:center;border-bottom:2px solid transparent;transition:all 0.15s; }
.oc-v-tab.active { color:${c};border-bottom-color:${c};font-weight:600; }

.oc-v-wave-container { display:flex;justify-content:center;align-items:center;height:160px;margin-bottom:8px; }
/* ChatGPT 风格动画 */
.oc-v-anim-idle, .oc-v-anim-thinking, .oc-v-anim-responding, .oc-v-anim-volume {
  display:flex;align-items:center;justify-content:center;width:100%;height:100%;
}
/* idle: 脉冲圆 */
.oc-v-anim-circle {
  width:64px;height:64px;border-radius:50%;background:${c};opacity:0.5;
  animation:oc-pulse-circle 1s ease-in-out infinite alternate;
}
@keyframes oc-pulse-circle { from{opacity:0.3;transform:scale(0.95)} to{opacity:0.8;transform:scale(1.05)} }
/* thinking: 环状脉冲 */
.oc-v-anim-ring {
  width:80px;height:80px;border-radius:50%;border:3px solid ${c};
  animation:oc-pulse-ring 0.8s ease-in-out infinite alternate;
}
@keyframes oc-pulse-ring { from{opacity:0.3;transform:scale(0.9)} to{opacity:1;transform:scale(1.2)} }
/* responding: brain SVG 旋转 */
.oc-v-anim-brain {
  animation:oc-rotate-brain 3s ease-in-out infinite;
  transform-origin:center center;
}
@keyframes oc-rotate-brain {
  0%{transform:rotate(0deg) scale(1)}
  25%{transform:rotate(5deg) scale(1.05)}
  75%{transform:rotate(-3deg) scale(1.02)}
  100%{transform:rotate(0deg) scale(1)}
}
/* volume: 声纹柱 */
.oc-v-bars { display:flex;align-items:flex-end;justify-content:center;gap:6px;height:80px; }
.oc-v-bar {
  width:10px;background:${c};border-radius:4px;min-height:4px;max-height:80px;
  transition:height 0.1s ease;
}
.oc-v-timer { text-align:center;font-size:28px;font-weight:300;color:var(--oc-text);font-variant-numeric:tabular-nums;margin-bottom:4px; }
.oc-v-status { font-size:13px;color:var(--oc-text-secondary);text-align:center;margin-bottom:12px; }

.oc-v-chat-list { max-height:240px;overflow-y:auto;padding:0 4px; }
.oc-v-chat-row { display:flex;margin-bottom:10px; }
.oc-v-chat-row.is-user { justify-content:flex-start; }
.oc-v-chat-row.is-agent { justify-content:flex-end; }
.oc-v-chat-bubble { max-width:80%;padding:10px 14px;border-radius:14px;font-size:13px;line-height:1.5;word-break:break-word; }
.oc-v-chat-row.is-user .oc-v-chat-bubble { background:var(--oc-bg);border:1px solid var(--oc-border);color:var(--oc-text);border-bottom-left-radius:4px; }
.oc-v-chat-row.is-agent .oc-v-chat-bubble { background:${c};color:#fff;border-bottom-right-radius:4px; }
.oc-v-chat-empty { text-align:center;font-size:13px;color:var(--oc-text-secondary);padding:30px 0; }

.oc-v-bottom-bar { display:flex;justify-content:center;margin-top:16px; }
.oc-v-hangup-btn { padding:12px 36px;border:none;border-radius:25px;background:#ef4444;color:#fff;font-size:15px;font-weight:600;cursor:pointer;display:flex;align-items:center;gap:6px;transition:all 0.15s; }
.oc-v-hangup-btn:hover { background:#dc2626; }

.oc-v-ended-icon { text-align:center;font-size:48px;margin-bottom:12px; }
.oc-v-ended-title { text-align:center;font-size:18px;font-weight:600;color:var(--oc-text);margin-bottom:8px; }
.oc-v-ended-duration { text-align:center;font-size:14px;color:var(--oc-text-secondary);margin-bottom:20px; }
.oc-v-close-btn { width:100%;padding:12px;border:1px solid var(--oc-border);border-radius:10px;background:transparent;color:var(--oc-text-secondary);font-size:14px;cursor:pointer;margin-top:10px; }
`;
}

/* ────── 工具函数 ────── */
function escHtml(s: string): string {
  return s.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}
function formatTime(sec: number): string {
  const m = Math.floor(sec / 60).toString().padStart(2, "0");
  const s = (sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}
function getPrimaryColor(): string {
  const root = getShadowRoot();
  if (root) {
    const c = getComputedStyle(root.host).getPropertyValue("--oc-primary").trim();
    if (c) return c;
  }
  return "#533afd";
}

/* ────── 发送验证码 ────── */
async function sendVerificationCode(): Promise<void> {
  const root = getShadowRoot();
  if (!root) return;
  const phone = (root.getElementById("oc-v-phone") as HTMLInputElement)?.value?.trim();
  if (!phone) { showError("请输入手机号码"); return; }
  phoneNumber = phone;

  const errEl = root.getElementById("oc-v-error");
  try {
    const origin = location.origin;
    const res = await fetch(`${origin}/api/auth/send-sms-code`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: JSON.stringify({ phone: `${selectedCountry.dial}${phone}` }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || data.message || "发送失败");
    }
    if (errEl) errEl.style.display = "none";
    codeSent = true;
    // 如果输入框里已有验证码，自动验证
    const codeInput = root.getElementById("oc-v-code") as HTMLInputElement;
    if (codeInput && codeInput.value.trim().length >= 4) {
      await verifyCode();
    }
    startCountdown();
  } catch (e: any) {
    showError(e.message || "验证码发送失败");
  }
}

function startCountdown(): void {
  stopCountdown();
  countdownSec = 60;
  const root = getShadowRoot();
  const btn = root?.getElementById("oc-v-sendcode") as HTMLButtonElement;
  if (btn) { btn.disabled = true; btn.textContent = `${countdownSec}s`; }
  countdownTimer = setInterval(() => {
    countdownSec--;
    if (btn) btn.textContent = `${countdownSec}s`;
    if (countdownSec <= 0) {
      stopCountdown();
      if (btn) { btn.disabled = false; btn.textContent = "验证码"; }
    }
  }, 1000);
}

function stopCountdown(): void {
  if (countdownTimer) { clearInterval(countdownTimer); countdownTimer = null; }
  countdownSec = 0;
}

/* ────── 验证验证码 ────── */
async function verifyCode(): Promise<boolean> {
  const root = getShadowRoot();
  if (!root) return false;
  const phone = (root.getElementById("oc-v-phone") as HTMLInputElement)?.value?.trim();
  const code = (root.getElementById("oc-v-code") as HTMLInputElement)?.value?.trim();
  if (!phone || !code) { showError("请输入手机号码和验证码"); return false; }
  phoneNumber = phone;

  try {
    const origin = location.origin;
    const dial = selectedCountry.dial;
    const res = await fetch(`${origin}/api/auth/verify-sms-code`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: JSON.stringify({ phone: `${dial}${phone}`, code }),
    });
    const data = await res.json().catch(() => ({}));
    if (!data.valid) throw new Error(data.error || "验证码错误");
    phoneVerified = true;
    const startBtn = root.getElementById("oc-v-start") as HTMLButtonElement;
    if (startBtn) {
      startBtn.style.background = getPrimaryColor();
      startBtn.style.cursor = "pointer";
    }
    return true;
  } catch {
    showError("验证码错误，请重试");
    return false;
  }
}

function showError(msg: string): void {
  const root = getShadowRoot();
  const el = root?.getElementById("oc-v-error");
  if (el) { el.style.display = "block"; el.textContent = msg; }
}

/* ────── 事件绑定 ────── */
function bindEvents(locale: Locale): void {
  storedLocale = locale;
  const root = getShadowRoot();
  if (!root) return;

  renderIdle();

  function renderIdle() {
    voiceState = "idle";
    phoneVerified = false;
    codeSent = false;
    chatMessages = [];
    callSec = 0;
    stopTimer();
    stopCountdown();
    setFabPulse(true);
    updateContent(buildIdleHTML());
    bindIdleEvents(locale);
  }

  function bindIdleEvents(locale: Locale): void {
    const sendBtn = root!.getElementById("oc-v-sendcode");
    const codeInput = root!.getElementById("oc-v-code");
    const startBtn = root!.getElementById("oc-v-start");
    const phoneInput = root!.getElementById("oc-v-phone") as HTMLInputElement;

    if (phoneInput) phoneInput.value = phoneNumber;

    root?.getElementById("oc-v-country")?.addEventListener("change", (e) => {
      const found = COUNTRIES.find(c => c.code === (e.target as HTMLSelectElement).value);
      if (found) selectedCountry = found;
    });

    sendBtn?.addEventListener("click", sendVerificationCode);

    // 输入验证码时自动验证（仅限已发送验证码后）
    codeInput?.addEventListener("input", async () => {
      const code = (codeInput as HTMLInputElement).value.trim();
      if (code.length >= 4 && codeSent) await verifyCode();
    });

    startBtn?.addEventListener("click", () => {
      if (!phoneVerified) {
        showError("请先获取并输入验证码");
        return;
      }
      renderDialing(locale);
    });
  }

  function renderDialing(locale: Locale): void {
    voiceState = "dialing";
    setFabPulse(false);
    updateContent(buildDialingHTML());

    const cancelBtn = root!.getElementById("oc-v-cancel");
    cancelBtn?.addEventListener("click", () => renderIdle());

    // 开始连接
    startVoiceCall(locale);
  }

  function renderConnected(locale: Locale): void {
    voiceState = "connected";
    updateContent(buildConnectedHTML());
    bindConnectedEvents(locale);
    startWaveformAnimation();
    startTimer();
  }

  function bindConnectedEvents(locale: Locale): void {
    const tabWave = root!.getElementById("oc-v-tab-waveform");
    const tabChat = root!.getElementById("oc-v-tab-chat");
    const hangupBtn = root!.getElementById("oc-v-hangup");

    tabWave?.addEventListener("click", () => {
      viewMode = "waveform";
      stopWaveformAnimation();
      updateContent(buildConnectedHTML());
      bindConnectedEvents(locale);
      startWaveformAnimation();
    });

    tabChat?.addEventListener("click", () => {
      viewMode = "chat";
      stopWaveformAnimation();
      updateContent(buildConnectedHTML());
      bindConnectedEvents(locale);
    });

    hangupBtn?.addEventListener("click", () => {
      endVoiceCall();
      showCallEnded();
    });
  }
}

/* ────── ChatGPT 风格动画 ────── */
function setAnimMode(mode: "idle" | "thinking" | "responding" | "volume"): void {
  animMode = mode;
  const root = getShadowRoot();
  if (!root) return;
  const idleEl = root.querySelector(".oc-v-anim-idle") as HTMLElement;
  const thinkingEl = root.querySelector(".oc-v-anim-thinking") as HTMLElement;
  const respondingEl = root.querySelector(".oc-v-anim-responding") as HTMLElement;
  const volumeEl = root.querySelector(".oc-v-anim-volume") as HTMLElement;
  if (idleEl) idleEl.style.display = mode === "idle" ? "" : "none";
  if (thinkingEl) thinkingEl.style.display = mode === "thinking" ? "" : "none";
  if (respondingEl) respondingEl.style.display = mode === "responding" ? "" : "none";
  if (volumeEl) volumeEl.style.display = mode === "volume" ? "" : "none";
}

function startWaveformAnimation(): void {
  setAnimMode("idle");

  // 持续检测音量，控制 volume / idle 切换
  function volumeLoop(): void {
    if (voiceState !== "connected") return;
    if (animMode === "responding" || animMode === "thinking") {
      animFrameId = requestAnimationFrame(volumeLoop);
      return;
    }
    let hasVolume = false;
    if (analyserNode) {
      const data = new Uint8Array(analyserNode.frequencyBinCount);
      analyserNode.getByteFrequencyData(data);
      const avg = data.reduce((a, b) => a + b, 0) / data.length;
      hasVolume = avg > 15;

      // 更新声纹柱
      if (animMode === "volume") {
        const root = getShadowRoot();
        for (let i = 0; i < 4; i++) {
          const bar = root?.getElementById(`oc-v-bar-${i}`) as HTMLElement;
          if (bar) {
            const v = data.slice(i * 8, (i + 1) * 8).reduce((a, b) => a + b, 0) / 8;
            const h = Math.max(4, Math.min(80, v * 1.2));
            bar.style.height = `${h}px`;
          }
        }
      }
    }

    if (hasVolume && animMode !== "volume") {
      setAnimMode("volume");
    } else if (!hasVolume && animMode === "volume") {
      // 延迟切回 idle，避免闪烁
      setTimeout(() => {
        if (animMode === "volume") setAnimMode("idle");
      }, 600);
    }
    animFrameId = requestAnimationFrame(volumeLoop);
  }

  animFrameId = requestAnimationFrame(volumeLoop);
}

function stopWaveformAnimation(): void {
  if (animFrameId) { cancelAnimationFrame(animFrameId); animFrameId = null; }
}

/* ────── 计时器 ────── */
function startTimer(): void {
  stopTimer();
  callSec = 0;
  const root = getShadowRoot();
  timerInterval = setInterval(() => {
    callSec++;
    const el = root?.getElementById("oc-v-timer");
    if (el) el.textContent = formatTime(callSec);
  }, 1000);
}

function stopTimer(): void {
  if (timerInterval) { clearInterval(timerInterval); timerInterval = null; }
  callSec = 0;
}

/* ────── WebSocket URL ────── */
function getVoiceWsUrl(): string {
  const overridden = (window as any).__ONECALL_VOICE_WS__;
  if (overridden) return overridden;
  const hostname = location.hostname;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return `ws://${hostname}:8765/ws`;
  }
  const protocol = location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${location.host}/ws`;
}

function bindConnectedEventsGlobal(): void {
  const root = getShadowRoot();
  if (!root) return;
  root.getElementById("oc-v-switch-text")?.addEventListener("click", () => {
    endVoiceCall();
    renderTextChatPanel(storedLocale, storedWidgetId, null, () => {
      // switch back to voice
      voiceState = "idle";
      updateContent(buildIdleHTML());
      bindIdleEventsStatic();
    });
  });
  root.getElementById("oc-v-tab-waveform")?.addEventListener("click", () => {
    if (viewMode === "waveform") return;
    viewMode = "waveform";
    stopWaveformAnimation();
    updateContent(buildConnectedHTML());
    bindConnectedEventsGlobal();
    startWaveformAnimation();
  });
  root.getElementById("oc-v-tab-chat")?.addEventListener("click", () => {
    if (viewMode === "chat") return;
    viewMode = "chat";
    stopWaveformAnimation();
    updateContent(buildConnectedHTML());
    bindConnectedEventsGlobal();
  });
  root.getElementById("oc-v-hangup")?.addEventListener("click", () => {
    manualHangup = true;
    endVoiceCall();
    showEndedUI();
  });
}

function showEndedUI(): void {
  voiceState = "ended";
  stopTimer();
  stopWaveformAnimation();
  setFabPulse(true);
  updateContent(buildEndedHTML());
  const root = getShadowRoot();
  root?.getElementById("oc-v-redial")?.addEventListener("click", () => {
    voiceState = "dialing";
    setFabPulse(false);
    updateContent(buildDialingHTML());
    root?.getElementById("oc-v-cancel")?.addEventListener("click", () => {
      voiceState = "idle";
      stopCountdown();
      setFabPulse(true);
      updateContent(buildIdleHTML());
      bindIdleEventsStatic();
    });
    startVoiceCall(storedLocale);
  });
  root?.getElementById("oc-v-close")?.addEventListener("click", () => {
    const panel = root?.querySelector(".oc-panel") as HTMLElement;
    if (panel) panel.style.display = "none";
    const overlay = root?.querySelector(".oc-overlay") as HTMLElement;
    if (overlay) overlay.style.display = "none";
  });
}

function bindIdleEventsStatic(): void {
  const root = getShadowRoot();
  if (!root) return;
  root.getElementById("oc-v-sendcode")?.addEventListener("click", sendVerificationCode);
  root.getElementById("oc-v-code")?.addEventListener("input", async () => {
    const ci = root!.getElementById("oc-v-code") as HTMLInputElement;
    if (ci.value.trim().length >= 4) await verifyCode();
  });
  root.getElementById("oc-v-start")?.addEventListener("click", () => {
    if (!phoneVerified) { showError("请先获取并输入验证码"); return; }
    voiceState = "dialing";
    setFabPulse(false);
    updateContent(buildDialingHTML());
    root?.getElementById("oc-v-cancel")?.addEventListener("click", () => {
      voiceState = "idle";
      setFabPulse(true);
      updateContent(buildIdleHTML());
      bindIdleEventsStatic();
    });
    startVoiceCall(storedLocale);
  });
}

/* ────── 开始语音通话 ────── */
async function startVoiceCall(locale: Locale): Promise<void> {
  const root = getShadowRoot();
  if (!root) return;

  try {
    micStream = await navigator.mediaDevices.getUserMedia({
      audio: { sampleRate: 16000, channelCount: 1, echoCancellation: true, noiseSuppression: true },
    });

    audioCtx = new AudioContext({ sampleRate: 16000 });
    analyserNode = audioCtx.createAnalyser();
    analyserNode.fftSize = 128;

    const wsUrl = getVoiceWsUrl();
    voiceWs = new WebSocket(wsUrl);
    voiceWs.binaryType = "arraybuffer";

    await new Promise<void>((resolve, reject) => {
      voiceWs!.onopen = () => {
        voiceWs!.send(JSON.stringify({
          provider: "widget",
          params: {
            agent_id: storedWidgetId,
            from: "zh",
            to: "zh",
            inputSampleRate: 16000,
            modalities: ["text", "audio"],
          },
        }));
      };
      voiceWs!.onmessage = (event) => {
        if (typeof event.data === "string") {
          try {
            const msg = JSON.parse(event.data);
            if (msg.type === "connected" || msg.type === "conversation_started" || msg.type === "greeting") {
              resolve();
            } else if (msg.type === "transcription") {
              const data = msg.data;
              if (data?.text) {
                const isFinal = data.isFinal;
                const role: "user" | "agent" = data.role === "agent" ? "agent" : "user";
                // 动画状态: agent说话→responding, user说话→thinking
                if (role === "agent") {
                  if (isFinal) setAnimMode("idle");
                  else setAnimMode("responding");
                } else {
                  setAnimMode("thinking");
                }
                if (isFinal && role === "agent") {
                  chatMessages.push({ role: "agent", text: data.text });
                } else if (isFinal && role === "user") {
                  chatMessages.push({ role: "user", text: data.text });
                }
                // 更新聊天视图
                if (viewMode === "chat" && voiceState === "connected") {
                  refreshChatView();
                }
              }
            } else if (msg.type === "error") {
              reject(new Error(msg.data?.message || msg.message || "未知错误"));
            }
          } catch {}
        } else if (event.data instanceof ArrayBuffer) {
          playPcm(event.data);
        }
      };
      voiceWs!.onerror = (e) => { console.error("[voice] WS error event"); reject(new Error("WebSocket 连接失败")); };
      voiceWs!.onclose = () => {
        if (manualHangup) { manualHangup = false; return; }
        if (voiceState === "connected") {
          endVoiceCall();
          showEndedUI();
        } else if (voiceState === "dialing") {
          // 连接过程中断开，回到 idle
          stopMicCapture();
          closeVoiceConnection();
          voiceState = "idle";
          setFabPulse(true);
          updateContent(buildIdleHTML());
          bindIdleEventsMuted();
        }
      };
    });

    // 连接成功
    voiceState = "connected";
    updateContent(buildConnectedHTML());
    bindConnectedEventsGlobal();
    startPcmCapture();
    startWaveformAnimation();
    startTimer();

  } catch (err: any) {
    console.error("[voice] 连接失败:", err.message);
    voiceState = "error";
    stopMicCapture();
    closeVoiceConnection();
    setFabPulse(true);
    updateContent(`<div class="oc-v-ended-icon">❌</div><p class="oc-v-ended-title">连接失败</p><p class="oc-v-ended-duration">${escHtml(err.message || "未知错误")}</p><button type="button" id="oc-v-retry" class="oc-v-start-btn" style="background:${getPrimaryColor()};">重新开始</button>`);
    const rt = getShadowRoot()?.getElementById("oc-v-retry");
    rt?.addEventListener("click", () => { voiceState = "idle"; renderIdle(); });
  }
}

function showCallEnded(): void {
  voiceState = "ended";
  stopTimer();
  stopWaveformAnimation();
  setFabPulse(true);
  closeVoiceConnection();
  updateContent(buildEndedHTML());

  const root = getShadowRoot();
  root?.getElementById("oc-v-redial")?.addEventListener("click", () => {
    // 重新对话：直接 dialing，跳过验证
    voiceState = "dialing";
    setFabPulse(false);
    updateContent(buildDialingHTML());
    root?.getElementById("oc-v-cancel")?.addEventListener("click", () => {
      voiceState = "idle";
      stopCountdown();
      setFabPulse(true);
      updateContent(buildIdleHTML());
      bindIdleEventsMuted();
    });
    startVoiceCall(_locale);
  });
  root?.getElementById("oc-v-close")?.addEventListener("click", () => {
    const panel = root?.querySelector(".oc-panel") as HTMLElement;
    if (panel) panel.style.display = "none";
    const overlay = root?.querySelector(".oc-overlay") as HTMLElement;
    if (overlay) overlay.style.display = "none";
  });
}

function refreshChatView(): void {
  const root = getShadowRoot();
  const list = root?.getElementById("oc-v-chat-list");
  if (!list) return;
  list.innerHTML = chatMessages.length === 0
    ? '<p class="oc-v-chat-empty">对话内容将在此显示</p>'
    : chatMessages.map(m => buildChatBubble(m)).join("");
  list.scrollTop = list.scrollHeight;
}

/* ────── PCM 音频采集与发送 ────── */
function startPcmCapture(): void {
  if (!audioCtx || !micStream) return;
  const source = audioCtx.createMediaStreamSource(micStream);
  analyserNode = audioCtx.createAnalyser();
  analyserNode.fftSize = 128;
  source.connect(analyserNode);

  // TODO: Migrate to AudioWorklet (ScriptProcessorNode is deprecated)
  scriptNode = audioCtx.createScriptProcessor(4096, 1, 1);

  scriptNode.onaudioprocess = (event) => {
    if (voiceState !== "connected" || !voiceWs || voiceWs.readyState !== WebSocket.OPEN) return;

    const input = event.inputBuffer.getChannelData(0);
    const inputRate = audioCtx!.sampleRate;
    const targetRate = 16000;

    let samples: Float32Array;
    if (Math.abs(inputRate - targetRate) < 100) {
      samples = new Float32Array(input);
      for (let i = 0; i < samples.length; i++) samples[i] = Math.max(-1, Math.min(1, samples[i]));
    } else {
      const ratio = targetRate / inputRate;
      const outLen = Math.floor(input.length * ratio);
      samples = new Float32Array(outLen);
      for (let i = 0; i < outLen; i++) {
        const srcIdx = i / ratio;
        const srcFloor = Math.floor(srcIdx);
        const frac = srcIdx - srcFloor;
        const a = input[Math.min(srcFloor, input.length - 1)];
        const b = input[Math.min(srcFloor + 1, input.length - 1)];
        samples[i] = Math.max(-1, Math.min(1, a + (b - a) * frac));
      }
    }

    for (let i = 0; i < samples.length; i++) {
      const s16 = Math.round(samples[i] * 32767);
      pcmSendBuf.push(s16 & 0xFF, (s16 >> 8) & 0xFF);
    }

    while (pcmSendBuf.length >= PCM_FRAME_BYTES) {
      const frame = new Uint8Array(pcmSendBuf.splice(0, PCM_FRAME_BYTES));
      try { voiceWs?.send(frame.buffer); } catch {}
    }
  };

  source.connect(scriptNode);
  scriptNode.connect(audioCtx.destination);
}

function stopMicCapture(): void {
  if (scriptNode) { scriptNode.disconnect(); scriptNode = null; }
  if (micStream) { micStream.getTracks().forEach(t => t.stop()); micStream = null; }
  pcmSendBuf = [];
}

/* ────── PCM 音频播放 ────── */
function playPcm(arrayBuf: ArrayBuffer): void {
  if (!audioCtx) return;
  if (audioCtx.state === "suspended") audioCtx.resume();

  const bytes = new Uint8Array(arrayBuf);
  let offset = 0;
  if (bytes.length > 44 && bytes[0] === 0x52 && bytes[1] === 0x49) offset = 44;

  const pcmLen = (bytes.length - offset) / 2;
  if (pcmLen <= 0) return;
  const pcm16 = new Int16Array(pcmLen);
  const view = new DataView(arrayBuf, offset);
  for (let i = 0; i < pcmLen; i++) pcm16[i] = view.getInt16(i * 2, true);

  const f32 = new Float32Array(pcmLen);
  for (let i = 0; i < pcmLen; i++) f32[i] = pcm16[i] / 32768;

  audioQueue.push(f32);
  if (!playing) playNext();
}

function playNext(): void {
  if (audioQueue.length === 0) { playing = false; return; }
  playing = true;
  const samples = audioQueue.shift()!;
  const buffer = audioCtx!.createBuffer(1, samples.length, 16000);
  buffer.getChannelData(0).set(samples);
  const source = audioCtx!.createBufferSource();
  source.buffer = buffer;
  source.connect(audioCtx!.destination);
  const now = audioCtx!.currentTime;
  const startTime = Math.max(now, nextPlayTime);
  source.start(startTime);
  nextPlayTime = startTime + buffer.duration;
  source.onended = () => playNext();
}

/* ────── 结束通话 ────── */
function endVoiceCall(): void {
  if (voiceState !== "connected") return;
  voiceState = "ended";
  stopMicCapture();
  voiceWs?.close();
  voiceWs = null;
  audioQueue = [];
  playing = false;
  nextPlayTime = 0;
  if (audioCtx && audioCtx.state !== "closed") {
    try { audioCtx.close(); } catch {}
    audioCtx = null;
  }
  analyserNode = null;
}
